import React from 'react'
import {
  Box,
  Button,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  makeStyles,
} from '@material-ui/core'
import {
  AddCircleOutline,
  DeleteOutline,
  VisibilityOutlined,
  VisibilityOffOutlined,
} from '@material-ui/icons'
import {
  FREQUENCY_OPTIONS,
  getFrequencyConfig,
  calculateMonthlyPrice,
  isAsNeeded,
  getFrequencyStat,
} from '../utils/frequencyConfig'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  serviceRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
    transition: 'opacity 0.2s',
  },
  serviceRowHidden: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.grey[300]}`,
    opacity: 0.5,
    transition: 'opacity 0.2s',
  },
  serviceFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    flex: 1,
  },
  serviceTopRow: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  nameField: {
    flex: 2,
  },
  freqField: {
    flex: 1,
    minWidth: 140,
  },
  priceField: {
    flex: 1,
    minWidth: 120,
  },
  serviceActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    marginTop: theme.spacing(0.5),
  },
  addButton: {
    alignSelf: 'flex-start',
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  hiddenBadge: {
    fontSize: 10,
    fontWeight: 600,
    color: theme.palette.warning.main,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  monthlyCalc: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  inclusionIncluded: {
    fontSize: 11,
    fontWeight: 600,
    color: '#2e7d32',
    marginTop: 2,
  },
  inclusionAdditional: {
    fontSize: 11,
    fontWeight: 600,
    color: '#e65100',
    marginTop: 2,
  },
}))

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0)

const ScopeEditor = ({ data, onChange }) => {
  const classes = useStyles()
  const services = data.services || []

  const handleServiceChange = (index, field, value) => {
    const updated = services.map((svc, i) =>
      i === index ? { ...svc, [field]: value } : svc
    )
    onChange('services', updated)
  }

  const handleFrequencyChange = (index, newFreq) => {
    const svc = services[index]
    const updates = { frequency: newFreq }

    // Auto-update first stat based on frequency
    const freqStat = getFrequencyStat(newFreq)
    if (freqStat && svc.stats && svc.stats.length > 0) {
      updates.stats = [freqStat, ...svc.stats.slice(1)]
    }

    // Zero out price for as-needed services
    if (isAsNeeded(newFreq)) {
      updates.perVisitPrice = 0
    }

    const updated = services.map((s, i) =>
      i === index ? { ...s, ...updates } : s
    )
    onChange('services', updated)
  }

  const handleToggleHidden = (index) => {
    const updated = services.map((svc, i) =>
      i === index ? { ...svc, hidden: !svc.hidden } : svc
    )
    onChange('services', updated)
  }

  const handleAddService = () => {
    onChange('services', [
      ...services,
      { name: '', shortDescription: '', description: '', imageUrl: '', frequency: 'Monthly', perVisitPrice: 0, hidden: false },
    ])
  }

  const handleRemoveService = (index) => {
    onChange(
      'services',
      services.filter((_, i) => i !== index)
    )
  }

  return (
    <Box className={classes.root}>
      <Typography variant="subtitle2">Services</Typography>

      {services.map((service, index) => {
        const asNeeded = isAsNeeded(service.frequency)
        const priceValue = service.perVisitPrice ?? service.monthlyPrice ?? ''
        const monthly = !asNeeded ? calculateMonthlyPrice(priceValue, service.frequency) : 0

        return (
          <Box
            key={index}
            className={service.hidden ? classes.serviceRowHidden : classes.serviceRow}
          >
            <Box className={classes.serviceFields}>
              <Box className={classes.serviceTopRow}>
                <TextField
                  label="Service Name"
                  value={service.name || ''}
                  onChange={(e) =>
                    handleServiceChange(index, 'name', e.target.value)
                  }
                  size="small"
                  variant="outlined"
                  fullWidth
                  className={classes.nameField}
                />
                <FormControl variant="outlined" size="small" className={classes.freqField}>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={service.frequency || ''}
                    onChange={(e) => handleFrequencyChange(index, e.target.value)}
                    label="Frequency"
                  >
                    {FREQUENCY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.value}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Per Visit Price"
                  value={asNeeded ? '' : (priceValue || '')}
                  onChange={(e) =>
                    handleServiceChange(index, 'perVisitPrice', e.target.value)
                  }
                  size="small"
                  variant="outlined"
                  fullWidth
                  type="number"
                  className={classes.priceField}
                  disabled={asNeeded}
                  placeholder={asNeeded ? '\u2014' : ''}
                />
              </Box>
              {!asNeeded && priceValue > 0 && (
                <Typography className={classes.monthlyCalc}>
                  Monthly: {formatCurrency(monthly)}
                </Typography>
              )}
              {service.frequency === 'As Needed - Included' && (
                <Typography className={classes.inclusionIncluded}>
                  Included w/ Mtc Plan
                </Typography>
              )}
              {service.frequency === 'As Needed - Additional' && (
                <Typography className={classes.inclusionAdditional}>
                  Additional Costs May Apply
                </Typography>
              )}
              <TextField
                label="Table Summary (short)"
                value={service.shortDescription || ''}
                onChange={(e) =>
                  handleServiceChange(index, 'shortDescription', e.target.value)
                }
                size="small"
                variant="outlined"
                fullWidth
                helperText="One-liner shown in the summary table"
              />
              <TextField
                label="Detail Page Description (full)"
                value={service.description || ''}
                onChange={(e) =>
                  handleServiceChange(index, 'description', e.target.value)
                }
                size="small"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
              />
              <TextField
                label="Image URL"
                value={service.imageUrl || ''}
                onChange={(e) =>
                  handleServiceChange(index, 'imageUrl', e.target.value)
                }
                size="small"
                variant="outlined"
                fullWidth
                placeholder="/images/proposals/filename.webp"
                helperText="Photo shown on the service detail page"
              />
              {service.hidden && (
                <Typography className={classes.hiddenBadge}>Hidden from proposal</Typography>
              )}
              {!service.hidden && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!service.hideDetailPage}
                      onChange={(e) => handleServiceChange(index, 'hideDetailPage', e.target.checked)}
                      color="default"
                      size="small"
                    />
                  }
                  label={<Typography variant="caption" style={{ fontSize: 11, color: '#888' }}>Table only (skip detail page)</Typography>}
                />
              )}
            </Box>
            <Box className={classes.serviceActions}>
              <Tooltip title={service.hidden ? 'Show in proposal' : 'Hide from proposal'}>
                <IconButton
                  size="small"
                  onClick={() => handleToggleHidden(index)}
                  color={service.hidden ? 'default' : 'primary'}
                >
                  {service.hidden ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Remove permanently">
                <IconButton
                  size="small"
                  onClick={() => handleRemoveService(index)}
                  style={{ color: '#ccc' }}
                >
                  <DeleteOutline style={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )
      })}

      <Button
        startIcon={<AddCircleOutline />}
        onClick={handleAddService}
        size="small"
        color="primary"
        className={classes.addButton}
      >
        Add Service
      </Button>

      <Box className={classes.switchRow}>
        <FormControlLabel
          control={
            <Switch
              checked={data.showAnnualTotal !== false}
              onChange={(e) => onChange('showAnnualTotal', e.target.checked)}
              color="primary"
              size="small"
            />
          }
          label="Show Total"
        />
      </Box>

      {data.showAnnualTotal !== false && (
        <TextField
          label="Total Label"
          value={data.annualTotalLabel || ''}
          onChange={(e) => onChange('annualTotalLabel', e.target.value)}
          size="small"
          variant="outlined"
          fullWidth
        />
      )}

      <TextField
        label="Notes (additional fees, exclusions, etc.)"
        value={data.notes || ''}
        onChange={(e) => onChange('notes', e.target.value)}
        size="small"
        variant="outlined"
        fullWidth
        multiline
        rows={3}
      />
    </Box>
  )
}

export default ScopeEditor
